import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import type {
  DashboardComputeResponse,
  MetricCatalogResponse,
  OperatorPreferenceResponse,
} from '@truecare/schemas';
import { unwrap } from '@truecare/api-client';
import { api } from '../api';
import { AdminLayout } from '../components/AdminLayout';
import { MetricCard } from '../components/MetricCard';
import { adminBeforeLoad } from '../lib/admin-before-load';

/**
 * Phase 14a — admin dashboard root.
 *
 * Auth gate via `beforeLoad`: any 401 redirects to the portal sign-in
 * URL with `?next=` per 14a-2; the portal validates the next URL
 * against its allowlist before honouring it.
 *
 * Data fetch: catalog + operator preferences load in parallel, then the
 * compute call requests the operator's preferred metric subset. Single
 * dashboard render = ≤3 round trips.
 */
export const Route = createFileRoute('/')({
  beforeLoad: () => adminBeforeLoad('/'),
  component: DashboardPage,
});

function DashboardPage() {
  const catalog = useQuery<MetricCatalogResponse>({
    queryKey: ['admin', 'dashboard', 'metrics'],
    queryFn: async () =>
      (await unwrap(
        api.GET('/api/v1/admin/dashboard/metrics' as never),
      )) as unknown as MetricCatalogResponse,
  });
  const prefs = useQuery<OperatorPreferenceResponse>({
    queryKey: ['operator', 'preferences'],
    queryFn: async () =>
      (await unwrap(
        api.GET('/api/v1/me/operator/preferences' as never),
      )) as unknown as OperatorPreferenceResponse,
  });

  const metricIds: string[] = prefs.data?.dashboardMetricIds ?? [];
  const compute = useQuery<DashboardComputeResponse>({
    queryKey: ['admin', 'dashboard', 'compute', metricIds],
    enabled: metricIds.length > 0,
    queryFn: async () =>
      (await unwrap(
        api.GET('/api/v1/admin/dashboard' as never, {
          params: { query: { metrics: metricIds.join(',') } },
        } as never),
      )) as unknown as DashboardComputeResponse,
  });

  return (
    <AdminLayout>
      <h1 className="font-heading text-3xl text-forest md:text-4xl">Dashboard</h1>
      <p className="mt-1 text-sm text-ink-muted">
        A morning-review snapshot of what needs your attention. Cards marked
        “View →” open the full list.
      </p>
      <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {metricIds.map((id: string) => {
          const definition = catalog.data?.items.find((m) => m.id === id);
          const result = compute.data?.results?.[id];
          if (!definition) return null;
          return <MetricCard key={id} definition={definition} result={result} />;
        })}
      </div>
    </AdminLayout>
  );
}
